import React, { useEffect, useMemo, useState } from 'react';
import type { InvitePayload, AppRoles, UserType } from '../types';
import { GRADE_LABELS, isCompanyDomain, USER_TYPE_LABELS } from '../types';
import { inviteUser } from '../services/inviteService';
import { fetchAllUnits, type Unit } from '../services/unitsService';
import { fetchAllUsers } from '../services/userService';

interface Props {
  open: boolean;
  onClose: () => void;
  onInvited: (uid: string, payload: InvitePayload) => void;
}

const OKR_ROLES  = ['viewer', 'editor', 'admin', 'superadmin'];
const EPR_ROLES  = ['employee', 'leader', 'hr_admin', 'admin', 'superadmin'];
const PORT_ROLES = ['viewer', 'admin', 'superadmin'];

const InviteUserModal: React.FC<Props> = ({ open, onClose, onInvited }) => {
  const [name, setName]           = useState('');
  const [email, setEmail]         = useState('');
  const [jobTitle, setJobTitle]   = useState('');
  const [grade, setGrade]         = useState('');
  const [department, setDept]     = useState('');
  const [okrRole, setOkrRole]     = useState('viewer');
  const [eprRole, setEprRole]     = useState('employee');
  const [portalRole, setPortal]   = useState('viewer');
  const [userType, setUserType]   = useState<UserType>('employee');
  const [isLeader, setIsLeader]   = useState(false);
  const [unitId, setUnitId]       = useState('');
  const [directManagerId, setDirectManagerId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  // Lookup data — fetched once when modal first opens. Lightweight (units
  // collection is tiny; user list is loaded already for autocomplete).
  const [units, setUnits]         = useState<Unit[]>([]);
  const [existingUsers, setExistingUsers] = useState<Array<{ id: string; name: string; email: string }>>([]);

  useEffect(() => {
    if (!open) return;
    void fetchAllUnits().then(setUnits).catch(() => {});
    void fetchAllUsers().then(users =>
      setExistingUsers(users.map(u => ({ id: u.id, name: u.name, email: u.email }))),
    ).catch(() => {});
  }, [open]);

  // Derived: unitName for cached display + manager match for autocomplete UI.
  const unitName = useMemo(() => units.find(u => u.id === unitId)?.name, [units, unitId]);

  const companyDomain = email.includes('@') && isCompanyDomain(email);

  const handleClose = () => {
    setName(''); setEmail(''); setJobTitle(''); setGrade(''); setDept('');
    setOkrRole('viewer'); setEprRole('employee'); setPortal('viewer'); setUserType('employee');
    setIsLeader(false); setUnitId(''); setDirectManagerId('');
    setError(''); setSuccess(''); setSubmitting(false);
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { setError('Name and email are required.'); return; }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) { setError('Please enter a valid email address.'); return; }

    setSubmitting(true);
    setError('');
    setSuccess('');

    const appRoles: AppRoles = {};
    if (okrRole)   appRoles.okr    = okrRole as AppRoles['okr'];
    if (eprRole)   appRoles.epr    = eprRole as AppRoles['epr'];
    if (portalRole) appRoles.portal = portalRole as AppRoles['portal'];

    const payload: InvitePayload = {
      name:     name.trim(),
      email:    email.trim().toLowerCase(),
      jobTitle: jobTitle.trim() || undefined,
      grade:    grade || undefined,
      department: department.trim() || undefined,
      appRoles,
      user_type: userType,
      isLeader,
      unitId:           unitId || undefined,
      unitName:         unitName || undefined,
      directManagerId:  directManagerId || undefined,
      // employeeLevel: omitted — Cloud Function derives from grade.
    };

    try {
      const { uid } = await inviteUser(payload);
      setSuccess(`Invitation sent to ${payload.email}`);
      onInvited(uid, payload);
      setTimeout(handleClose, 1800);
    } catch (err: any) {
      setError(err.message || 'Failed to send invitation. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const selectCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#011132]/20 focus:border-[#011132] bg-white';
  const inputCls  = selectCls;
  const labelCls  = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />

      {/* Modal */}
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div style={{ background: '#011132' }} className="px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-white font-bold text-base">Invite New User</h2>
            <p className="text-white/60 text-xs mt-0.5">An invitation email will be sent automatically</p>
          </div>
          <button onClick={handleClose} className="text-white/60 hover:text-white transition">
            <span className="material-symbols-rounded" style={{ fontSize: 20 }}>close</span>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">

          {/* Name + Email */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Full Name <span className="text-red-400">*</span></label>
              <input value={name} onChange={e => setName(e.target.value)} className={inputCls} placeholder="e.g. Budi Santoso" required />
            </div>
            <div>
              <label className={labelCls}>Email <span className="text-red-400">*</span></label>
              <input value={email} onChange={e => setEmail(e.target.value)} className={inputCls} type="email" placeholder="user@company.com" required />
            </div>
          </div>

          {/* MS SSO chip */}
          {companyDomain && (
            <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2.5">
              <span className="material-symbols-rounded text-blue-600" style={{ fontSize: 16 }}>verified_user</span>
              <p className="text-xs text-blue-700 font-medium">
                This user must sign in via <strong>Microsoft 365 SSO</strong> — their invite email will include an MS SSO link.
              </p>
            </div>
          )}

          {/* Job Title + Department */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Job Title</label>
              <input value={jobTitle} onChange={e => setJobTitle(e.target.value)} className={inputCls} placeholder="e.g. Marketing Manager" />
            </div>
            <div>
              <label className={labelCls}>Department</label>
              <input value={department} onChange={e => setDept(e.target.value)} className={inputCls} placeholder="e.g. Marketing" />
            </div>
          </div>

          {/* Unit + Direct Manager — required for OKR Lead Dept resolution +
              EPR magic-link panel suggestion. Picker from shared /units +
              existing /users collections so admin doesn't type IDs. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Unit (Lead Dept)</label>
              <select value={unitId} onChange={e => setUnitId(e.target.value)} className={selectCls}>
                <option value="">— None —</option>
                {units.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Direct Manager</label>
              <select value={directManagerId} onChange={e => setDirectManagerId(e.target.value)} className={selectCls}>
                <option value="">— None —</option>
                {existingUsers.map(u => (
                  <option key={u.id} value={u.id}>{u.name} · {u.email}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Is Leader — gates OKR Tracker dept-lead features (recap email
              auto-include, leader dashboard, notification settings). */}
          <label className="flex items-center gap-2.5 px-3 py-2.5 bg-stone-50 border border-stone-200 rounded-lg cursor-pointer hover:bg-stone-100 transition">
            <input
              type="checkbox"
              checked={isLeader}
              onChange={e => setIsLeader(e.target.checked)}
              className="w-4 h-4 rounded border-stone-300 text-[#011132] focus:ring-2 focus:ring-[#011132]/20"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-gray-800">Mark as Leader</p>
              <p className="text-xs text-gray-500">Unlocks dept-lead surfaces in OKR Tracker + auto-includes in weekly/monthly reminder roster.</p>
            </div>
          </label>

          {/* User Type */}
          <div>
            <label className={labelCls}>User Type</label>
            <select value={userType} onChange={e => setUserType(e.target.value as UserType)} className={selectCls}>
              {Object.entries(USER_TYPE_LABELS).map(([id, label]) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>
          </div>

          {/* Grade */}
          <div>
            <label className={labelCls}>HRIS Grade</label>
            <select value={grade} onChange={e => setGrade(e.target.value)} className={selectCls}>
              <option value="">— Not assigned —</option>
              {Object.entries(GRADE_LABELS).map(([id, label]) => (
                <option key={id} value={id}>{label}</option>
              ))}
            </select>
          </div>

          {/* Roles */}
          <div>
            <p className={labelCls}>App Access</p>
            <div className="space-y-2 bg-gray-50 border border-gray-200 rounded-xl p-3">
              {[
                { label: 'OKR Tracker', roles: OKR_ROLES,  value: okrRole,   set: setOkrRole },
                { label: 'EPR',         roles: EPR_ROLES,  value: eprRole,   set: setEprRole },
                { label: 'Leadership Portal', roles: PORT_ROLES, value: portalRole, set: setPortal },
              ].map(({ label, roles, value, set }) => (
                <div key={label} className="flex items-center gap-3">
                  <span className="text-sm text-gray-600 w-36 flex-shrink-0">{label}</span>
                  <select value={value} onChange={e => set(e.target.value)} className={selectCls + ' flex-1'}>
                    {roles.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          {/* Error / Success */}
          {error && (
            <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5">
              <span className="material-symbols-rounded flex-shrink-0" style={{ fontSize: 16 }}>error</span>
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2.5">
              <span className="material-symbols-rounded flex-shrink-0" style={{ fontSize: 16 }}>check_circle</span>
              {success}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex gap-3">
          <button type="button" onClick={handleClose} className="flex-1 py-2.5 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition">
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex-1 py-2.5 text-sm font-semibold text-white rounded-lg transition disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: '#011132' }}
          >
            {submitting
              ? <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> Sending…</>
              : <><span className="material-symbols-rounded" style={{ fontSize: 16 }}>send</span> Send Invitation</>
            }
          </button>
        </div>
      </div>
    </div>
  );
};

export default InviteUserModal;
